"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { EASE_OUT_EXPO } from "@/components/motion/constants";

const PALETTE_CONFIG = {
  contact: {
    clipId: "contact-hero-organic-clip",
    strokeVar: "var(--contact-primary)",
    glowVar: "var(--contact-primary)",
  },
  site: {
    clipId: "site-hero-organic-clip",
    strokeVar: "var(--site-primary)",
    glowVar: "var(--site-primary)",
  },
} as const;

function isDataUrl(src: string) {
  return src.startsWith("data:");
}

interface HeroOrganicMaskProps {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  priority?: boolean;
  palette?: keyof typeof PALETTE_CONFIG;
}

export function HeroOrganicMask({
  src,
  alt,
  className,
  imageClassName,
  priority = false,
  palette = "contact",
}: HeroOrganicMaskProps) {
  const reduced = useReducedMotion();
  const { clipId, strokeVar, glowVar } = PALETTE_CONFIG[palette];

  return (
    <div className={cn("relative mx-auto w-full max-w-lg", className)}>
      <svg aria-hidden className="absolute size-0 overflow-hidden">
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path d="M0.08,0.06 C0.28,0.02 0.52,0.04 0.72,0.08 C0.88,0.11 0.96,0.22 0.97,0.38 C0.98,0.55 0.94,0.72 0.82,0.84 C0.68,0.97 0.48,0.99 0.32,0.96 C0.18,0.93 0.06,0.82 0.04,0.66 C0.02,0.48 0.03,0.28 0.08,0.06 Z" />
          </clipPath>
        </defs>
      </svg>

      <motion.div
        initial={reduced ? false : { scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1, ease: EASE_OUT_EXPO }}
        className="relative aspect-[4/5] w-full"
        style={{
          clipPath: `url(#${clipId})`,
          WebkitClipPath: `url(#${clipId})`,
        }}
      >
        {isDataUrl(src) ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={src}
            alt={alt}
            className={cn("h-full w-full object-cover", imageClassName)}
          />
        ) : (
          <Image
            src={src}
            alt={alt}
            fill
            className={cn("object-cover", imageClassName)}
            priority={priority}
            sizes="(max-width: 768px) 100vw, 480px"
          />
        )}
      </motion.div>

      <svg
        aria-hidden
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <path
          d="M8,6 C28,2 52,4 72,8 C88,11 96,22 97,38 C98,55 94,72 82,84 C68,97 48,99 32,96 C18,93 6,82 4,66 C2,48 3,28 8,6 Z"
          fill="none"
          stroke={strokeVar}
          strokeWidth="0.6"
          opacity="0.45"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div
        aria-hidden
        className="pointer-events-none absolute -inset-4 -z-10 rounded-[40%] opacity-[0.1] blur-3xl"
        style={{ backgroundColor: glowVar }}
      />
    </div>
  );
}
